/**
 * What changed in a table, cell by cell.
 *
 * Rows are matched by their label and columns by their heading, as a reader
 * would find them (and as a refresh from CSRD does), so a row added in the
 * middle does not make every row below it read as changed. A figure that
 * changed says by how much.
 *
 * Pure. Two tables in, a description of the difference out.
 */

import { pairedBy } from '@/features/csrdLink/restyle'
import type { TableCell, TableSection } from '@/domain/section'

export type CellStatus = 'same' | 'changed' | 'added'

export interface CellDiff {
  /** What the cell says now (or said, for a row that is gone). */
  readonly value: string
  /** What it said, for a cell that changed. */
  readonly before: string | null
  readonly status: CellStatus
  /** By how much a figure moved, when it and what it was are both figures. */
  readonly delta: number | null
}

export type RowStatus = 'same' | 'changed' | 'added' | 'removed'

export interface RowDiff {
  readonly status: RowStatus
  readonly cells: readonly CellDiff[]
}

export interface TableDiff {
  readonly header: readonly CellDiff[]
  readonly rows: readonly RowDiff[]
  readonly changedCells: number
  readonly addedRows: number
  readonly removedRows: number
}

/**
 * A figure as it is printed: `7 934`, `-1 467`, `(1 467)`, `4,5`. Null for anything else.
 */
export function parseFigure(text: string): number | null {
  const compact = text.replace(/[\s  ]/g, '').replace('−', '-')
  const negative = /^\(.*\)$/.test(compact)
  const digits = (negative ? compact.slice(1, -1) : compact).replace(',', '.')
  if (!/^-?\d+(\.\d+)?$/.test(digits)) return null
  const value = Number(digits)
  return negative ? -value : value
}

function compared(now: TableCell | undefined, was: TableCell | undefined): CellDiff {
  const value = now?.value ?? ''
  if (was === undefined) return { value, before: null, status: 'added', delta: null }
  if (was.value === value) return { value, before: null, status: 'same', delta: null }

  const left = parseFigure(was.value)
  const right = parseFigure(value)
  const delta = left !== null && right !== null ? right - left : null
  return { value, before: was.value, status: 'changed', delta }
}

function row(
  cells: readonly TableCell[],
  was: readonly TableCell[] | undefined,
  columns: readonly (number | undefined)[],
): RowDiff {
  const diffs = cells.map((cell, at) => {
    if (was === undefined) return compared(cell, undefined)
    const from = columns[at]
    return compared(cell, from === undefined ? undefined : was[from])
  })
  if (was === undefined) return { status: 'added', cells: diffs }
  return {
    status: diffs.some((cell) => cell.status !== 'same') ? 'changed' : 'same',
    cells: diffs,
  }
}

/** What changed between two versions of a table. */
export function diffTable(before: TableSection, after: TableSection): TableDiff {
  const columns = pairedBy(
    before.header.map((cell) => cell.value),
    after.header.map((cell) => cell.value),
    true,
  )

  const header = after.header.map((cell, at) => {
    const from = columns[at]
    return compared(cell, from === undefined ? undefined : before.header[from])
  })

  const rowsAt = pairedBy(
    before.rows.map((cells) => cells[0]?.value ?? ''),
    after.rows.map((cells) => cells[0]?.value ?? ''),
    false,
  )
  const kept = new Set(rowsAt.filter((at): at is number => at !== undefined))

  // A row that is gone is shown where it was: right after the row it followed.
  const goneAfter = new Map<number, RowDiff[]>()
  let following = -1
  before.rows.forEach((cells, at) => {
    const now = rowsAt.indexOf(at)
    if (kept.has(at)) {
      following = now
      return
    }
    const gone: RowDiff = {
      status: 'removed',
      cells: cells.map((cell) => ({
        value: cell.value,
        before: null,
        status: 'same',
        delta: null,
      })),
    }
    goneAfter.set(following, [...(goneAfter.get(following) ?? []), gone])
  })

  const rows: RowDiff[] = [...(goneAfter.get(-1) ?? [])]
  after.rows.forEach((cells, at) => {
    const from = rowsAt[at]
    rows.push(row(cells, from === undefined ? undefined : before.rows[from], columns))
    rows.push(...(goneAfter.get(at) ?? []))
  })

  const cellsOf = [header, ...rows.map((one) => one.cells)]
  return {
    header,
    rows,
    changedCells: cellsOf.flat().filter((cell) => cell.status === 'changed').length,
    addedRows: rows.filter((one) => one.status === 'added').length,
    removedRows: rows.filter((one) => one.status === 'removed').length,
  }
}

/**
 * The cells of `after` that differ from `before`, by where they are drawn.
 *
 * The key is `line:column`, a line being 0 for the header and the row's place
 * plus one for the body, the way the table is drawn (`spans.ts`). Cells that
 * are the same are left out; a cell of a row or a column that was not there is
 * `added`.
 */
export function cellChanges(
  before: TableSection,
  after: TableSection,
): ReadonlyMap<string, CellDiff> {
  const diff = diffTable(before, after)
  const found = new Map<string, CellDiff>()

  const note = (line: number, cells: readonly CellDiff[]): void => {
    cells.forEach((cell, column) => {
      if (cell.status !== 'same') found.set(`${line}:${column}`, cell)
    })
  }

  note(0, diff.header)
  // A row that is gone is not drawn, so it takes no line.
  let line = 1
  for (const one of diff.rows) {
    if (one.status === 'removed') continue
    note(line, one.cells)
    line += 1
  }
  return found
}
